import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Button from '../../components/ui/Button';
import InputWithIcon from '../../components/ui/InputWithIcon';
import OrderNewItem from '../order/OrderNewItem';

// 🔹 Modal for Adding New Customer
const NewCustomerModal = ({ open, onClose, onSave }) => {
  const [customer, setCustomer] = useState('');
  const [mobile, setMobile] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const customerRef = useRef(null);
  const mobileRef = useRef(null);
  const shortCodeRef = useRef(null);
  const addressRef = useRef(null);
  const emailRef = useRef(null);
  const saveBtnRef = useRef(null);

   useEffect(() => {
    if (open && customerRef.current) {
      setTimeout(() => customerRef.current.focus(), 50);
    }
  }, [open]);

  const handleSave = async () => {
    if (!customer || !mobile) {
      setError('Customer name and Mobile No are required.');
      return;
    }
    setError('');
    setSaving(true);

    try {
      const payload = {
        StateCode: "",
        Vid: 0,
        VCode: shortCode || mobile || "0",
        Vname: customer,
        Address1: address || "",
        City: "",
        State: "",
        Pincode: "",
        GST: "",
        VType: "Customer",
        Phone: mobile,
        Mail: email || "",
        Mobile: mobile,
        Active: 1,
        ComId: sessionStorage.getItem("comid") || "1",
        Sp_SqlAction: "insert"
      };

      const res = await api.saveCustomer(payload);
      const body = res?.DMLVendorOrim?.Table?.[0] ?? {};

      if (body.Status === "Error") {
        setError(body.Message || "Failed to create customer.");
      } else {
        const savedCustomer = {
          ...payload,
          Vid: body.Vid,
          VCode: body.VCode || payload.VCode || payload.Mobile || '0',
          Balance: 0,
        };
        onSave(savedCustomer);
        onClose();
      }
    } catch (err) {
      console.error("Create customer error", err);
      setError("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
    }
  };

  if (!open) return null;

  
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Add New Customer</h3>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="space-y-4">
          <InputWithIcon
            label="Customer*"
            value={customer}
            ref={customerRef}
            onChange={(e) => setCustomer(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, mobileRef)}
          />
          <InputWithIcon
            label="Mobile No*"
            value={mobile}
            ref={mobileRef}
            onChange={(e) => setMobile(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, shortCodeRef)}
          />
          <InputWithIcon
            label="Short Code"
            value={shortCode}
            ref={shortCodeRef}
            onChange={(e) => setShortCode(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, addressRef)}
          />
          <InputWithIcon
            label="Address"
            value={address}
            ref={addressRef}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, emailRef)}
          />
          <InputWithIcon
            label="Email"
            type="email"
            value={email}
            ref={emailRef}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, saveBtnRef)}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            ref={saveBtnRef}
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1"
          >
            {saving ? 'Saving...' : '💾 Save'}
          </Button>

          <Button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 px-3 py-1">
            ❌ Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddSalesOrderPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();

  const customerInputRef = useRef(null);
  const createOrderBtnRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [editableMobile, setEditableMobile] = useState('');

  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [editableAddress, setEditableAddress] = useState('');
  const [refNo, setRefNo] = useState('');
  const [amount, setAmount] = useState('');
  const [payMode, setPayMode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add Item modal state
  const [openAddItemModal, setOpenAddItemModal] = useState(false);
  const [editingContextLocal, setEditingContextLocal] = useState(null);

  const gdId = sessionStorage.getItem('gdid');
  const [focusAddBtn, setFocusAddBtn] = useState(false);

  // Sales types
  const [salesTypes, setSalesTypes] = useState([]);
  const [salesType, setSalesType] = useState('');
  const [salesTypeQuery, setSalesTypeQuery] = useState('');
  const [salesDropdownOpen, setSalesDropdownOpen] = useState(false);

  // New customer modal
  const [openCustomerModal, setOpenCustomerModal] = useState(false);

  // Focus customer input on mount
  useEffect(() => {
    if (customerInputRef.current) customerInputRef.current.focus();
  }, []);

  // Fetch customers and payment modes
  useEffect(() => {
  let mounted = true;
  const fetchData = async () => {
    try {
      const res = await api.getCustomerListWithBalanceTax(user.UsrName);
      const body = res?.CustomerListWithBalanceTaxjs ?? res ?? {};

      const t = body.Table ?? [];
      const t1 = body.Table1 ?? [];
      const t2 = body.Table2 ?? [];

      if (!mounted) return;
      // ✅ Include VCode for all customers
      setCustomers(t.map(c => ({
        ...c,
        VCode: c.VCode || c.Mobile || '0' // fallback if API doesn't provide
      })));

      setPaymentModes(t1 || []);
      setSalesTypes(t2 || []);

      if (t1?.length) setPayMode(prev => prev || t1[0].Mode || '');
      if (t2?.length) {
        const def = t2[0];
        setSalesType(prev => prev || def.TypeStr || '');
        setSalesTypeQuery(prev => prev || def.TypeName || '');
      }
    } catch (err) {
      console.error('Failed to fetch customers', err);
      if (!mounted) return;
      setError('Failed to fetch customers.');
    } finally {
      if (mounted) setLoading(false);
    }
  };

  fetchData();
  return () => { mounted = false; };
}, [user.UsrName]);

  // Filter customers
  useEffect(() => {
    if (customerSearch) {
      const results = customers.filter(c =>
        c.Vname.toLowerCase().includes(customerSearch.toLowerCase()) ||
        (c.Mobile || '').includes(customerSearch)
      );
      const exactMatch = selectedCustomer && selectedCustomer.Vname.toLowerCase() === customerSearch.toLowerCase();
      setFilteredCustomers(exactMatch ? [] : results);
      setIsDropdownOpen(!exactMatch && results.length > 0);
      setHighlightIndex(-1);
    } else {
      setFilteredCustomers(customers);
      setIsDropdownOpen(customers.length > 0);
      setHighlightIndex(-1);
    }
  }, [customerSearch, customers, selectedCustomer]);

  const handleCustomerSelect = (customer) => {
   setSelectedCustomer({
    ...customer,
    VCode: customer.VCode || customer.Mobile || '0'
  });
    setCustomerSearch(customer.Vname);
    setEditableAddress(`${customer.Vname}\n${customer.Address1 || '-'}, ${customer.City || '-'}`);
    setEditableMobile(customer.Mobile || '');
    setIsDropdownOpen(false);
    setFilteredCustomers([]);
    setHighlightIndex(-1);

    setTimeout(() => {
      if (createOrderBtnRef.current) createOrderBtnRef.current.focus();
    }, 50);
  };

  // Save new customer
  const handleSaveCustomer = (newCustomer) => {
    setCustomers((prev) => [...prev, newCustomer]);
    setSelectedCustomer(newCustomer);
    setCustomerSearch(newCustomer.Vname);
    setEditableAddress(newCustomer.Address1);
    setEditableMobile(newCustomer.Mobile);
  };

  // Keyboard nav
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredCustomers[highlightIndex]) {
        handleCustomerSelect(filteredCustomers[highlightIndex]);
      } else if (filteredCustomers.length === 1) {
        handleCustomerSelect(filteredCustomers[0]);
      } else if (selectedCustomer) {
        handleCreateSalesOrder();
      }
      return;
    }

    if (!isDropdownOpen || filteredCustomers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev < filteredCustomers.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredCustomers.length - 1));
    }
  };

  // Create Sales Order
  const handleCreateSalesOrder = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer.');
      return;
    }

    const amountAsNumber = parseFloat(amount);
    if (amount && (isNaN(amountAsNumber) || amountAsNumber < 0)) {
      setError('Please enter a valid amount.');
      return;
    }

    if (!gdId) {
      setError('Godown not selected. Please select a godown first.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const selType = salesTypes.find(t => t.TypeStr === salesType) || { TypeStr: '', TypeName: '' };

      const payload = {
        User: user.UsrName,
        Vid: selectedCustomer.Vid,
        VName: selectedCustomer.Vname,
        Address: editableAddress,
        Phone: editableMobile,
        OrderDate: orderDate,
        SessionId: '9944010155',
        amountPaid: amount ? parseFloat(amount) : 0,
        Paymode: payMode || '',
        Refno: refNo || '',
        GdId: Number(gdId),
        SubTypecode: selType.TypeStr,
        Subtypename: selType.TypeName,
         VCode: selectedCustomer.VCode || '0',
         
      };
      
      console.debug('createSalesOrder payload:', payload);
      const createResult = await api.createSalesOrder(payload);

      const orderTable =
        createResult?.Table ??
        createResult?.MobCreateorderlistTaxjs?.Table ??
        createResult?.data?.MobCreateorderlistTaxjs?.Table ??
        [];

      if (orderTable.length > 0) {
        const newOrderId = orderTable[0].POId;
        const selectedOrderData = await api.getSelectedSalesOrder(newOrderId);

        const ctx = {
          id: newOrderId,
          customerId: selectedCustomer.Vid,
          orderDate,
          customer: selectedCustomer,
          orderDetails: selectedOrderData
        };

        setEditingContext(ctx);
        setEditingContextLocal(ctx);
        setSuccess(`Sales Order created successfully with ID: ${newOrderId}`);
        setFocusAddBtn(true);

        setTimeout(() => {
          setView('new-salesorder');
          setOpenAddItemModal(true);
        }, 100);
      } else {
        setError('Failed to create order.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // filter sales type list
  const filteredSalesTypes = salesTypes.filter(t =>
    t.TypeName.toLowerCase().includes(salesTypeQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="p-5 bg-white shadow-md border-b">
        <h2 className="text-2xl font-bold text-center text-gray-800 tracking-tight">📝 Create New Sales Order</h2>
      </div>

      <div className="flex justify-center mt-8">
        <div className="w-full max-w-xl p-3 bg-white shadow-lg rounded-2xl">

          {/* Delivery Date + New Customer */}
          <div className="mb-6 flex items-end justify-between">
            <div className="w-full">
              <label className="block mb-2 text-sm font-semibold text-gray-700">📅 Delivery Date*</label>
              <input
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <Button
              onClick={() => setOpenCustomerModal(true)}
              className="ml-2 px-3 py-1 text-sm font-semibold bg-blue-500 hover:bg-blue-700"
            >
               New Customer
            </Button>
          </div>

          {/* Customer Search */}
          <div className="relative mb-6 text-gray-500 text-sm">
            <InputWithIcon
              label="Search & Select Customer*"
              value={customerSearch}
              ref={customerInputRef}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>}
            />
            {isDropdownOpen && filteredCustomers.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto divide-y divide-gray-100">
                {filteredCustomers.map((c, idx) => (
                  <li
                    key={c.Vid}
                    onMouseDown={() => handleCustomerSelect(c)}
                    className={`flex flex-col px-3 py-2 cursor-pointer transition-all duration-150 ${idx === highlightIndex ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-blue-50 hover:text-blue-600'
                      } rounded-lg`}
                  >
                    <span className="font-medium text-gray-800">{c.Vname}</span>
                    <span className="text-xs text-gray-500">{c.Mobile}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Customer Details */}
          {selectedCustomer && (
            <div className="p-1 mb-6 bg-white rounded-xl shadow-sm">
              <label className="block mb-2 text-sm font-semibold text-gray-800">Delivery Address*</label>
              <textarea
                value={editableAddress}
                onChange={(e) => setEditableAddress(e.target.value)}
                rows="3"
                className="w-full p-3 mb-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="space-y-2 text-sm">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-800">📞 Mobile*</label>
                  <input
                    type="text"
                    value={editableMobile}
                    onChange={(e) => setEditableMobile(e.target.value)}
                    className="w-full p-3 mb-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <p>
                  <span className="font-medium text-gray-800">💰 Current Balance:</span>
                  <span className={`ml-1 font-bold ${selectedCustomer.Balance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedCustomer.Balance.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                  </span>
                </p>
              </div>
              {/* Payment Section */}
              <div className="mt-6">
                <h4 className="flex items-center gap-2 pb-2 mb-4 text-base font-semibold text-gray-800">💳 Payment (Optional)</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <InputWithIcon label="Ref No" value={refNo} onChange={(e) => setRefNo(e.target.value)} />
                  <InputWithIcon label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />

                  <div>
                    <label className="block mb-2 text-sm font-bold text-gray-700">Pay Mode</label>
                    <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {paymentModes.map((mode, idx) => (
                        <option key={idx} value={mode.Mode}>{mode.Mode}</option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ Searchable Sales Type */}
                  <div className="relative">
                    <label className="block mb-2 text-sm font-bold text-gray-700">Sales Type</label>
                    <input
                      value={salesTypeQuery}
                      onChange={(e) => {
                        setSalesTypeQuery(e.target.value);
                        setSalesDropdownOpen(true);
                      }}
                      onFocus={() => setSalesDropdownOpen(true)}
                      placeholder="Search sales type..."
                      className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {salesDropdownOpen && (
                      <ul className="absolute z-10 w-full bg-white border rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg">
                        {filteredSalesTypes.length > 0 ? (
                          filteredSalesTypes.map((type) => (
                            <li
                              key={type.TypeStr}
                              onClick={() => {
                                setSalesType(type.TypeStr);
                                setSalesTypeQuery(type.TypeName);
                                setSalesDropdownOpen(false);
                              }}
                              className="p-2 hover:bg-blue-100 cursor-pointer"
                            >
                              {type.TypeName}
                            </li>
                          ))
                        ) : (
                          <li className="p-2 text-gray-500">No results</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3 justify-end">
            <Button ref={createOrderBtnRef} onClick={handleCreateSalesOrder} disabled={!selectedCustomer || loading} className="px-5 py-2 text-sm font-semibold">
              {loading ? 'Creating...' : '✅ Create Sales Order'}
            </Button>
            <Button onClick={() => setView('salesorders')} className="px-5 py-2 text-sm font-semibold bg-gray-500 hover:bg-gray-600">❌ Cancel</Button>
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      {editingContextLocal && (
        <OrderNewItem
          setView={setView}
          context={editingContextLocal}
          user={user}
          setSuccess={setSuccess}
          setError={setError}
          openModal={openAddItemModal}
          setOpenModal={setOpenAddItemModal}
          focusAddBtn={focusAddBtn}
        />
      )}

      {/* New Customer Modal */}
      <NewCustomerModal
        open={openCustomerModal}
        onClose={() => setOpenCustomerModal(false)}
        onSave={handleSaveCustomer}
      />
    </div>
  );
};

export default AddSalesOrderPage;



// import React, { useState, useEffect, useRef } from 'react';
// import { useAuth } from '../../context/AuthContext';
// import api from '../../api/api';
// import Button from '../../components/ui/Button';
// import InputWithIcon from '../../components/ui/InputWithIcon';
// import OrderNewItem from '../order/OrderNewItem'; // 👈 For Add Item modal

// const AddSalesOrderPage = ({ setView, setEditingContext }) => {
//   const { user } = useAuth();

//   const customerInputRef = useRef(null);
//   const createOrderBtnRef = useRef(null);

//   const [customers, setCustomers] = useState([]);
//   const [paymentModes, setPaymentModes] = useState([]);
//   const [customerSearch, setCustomerSearch] = useState('');
//   const [filteredCustomers, setFilteredCustomers] = useState([]);
//   const [selectedCustomer, setSelectedCustomer] = useState(null);
//   const [isDropdownOpen, setIsDropdownOpen] = useState(true);
//   const [highlightIndex, setHighlightIndex] = useState(-1);

//   const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
//   const [editableAddress, setEditableAddress] = useState('');
//   const [refNo, setRefNo] = useState('');
//   const [amount, setAmount] = useState('');
//   const [payMode, setPayMode] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');

//   // Add Item modal state
//   const [openAddItemModal, setOpenAddItemModal] = useState(false);
//   const [editingContextLocal, setEditingContextLocal] = useState(null);

//   const gdId = sessionStorage.getItem('gdid');
//   const [focusAddBtn, setFocusAddBtn] = useState(false);

//   // Sales types
//   const [salesTypes, setSalesTypes] = useState([]);
//   const [salesType, setSalesType] = useState(''); // stores TypeStr
//   const [salesTypeQuery, setSalesTypeQuery] = useState('');
//   const [salesDropdownOpen, setSalesDropdownOpen] = useState(false);

//   // Focus customer input on mount
//   useEffect(() => {
//     if (customerInputRef.current) customerInputRef.current.focus();
//   }, []);

//   // Fetch customers and payment modes
//   useEffect(() => {
//     let mounted = true;
//     const fetchData = async () => {
//       try {
//         const res = await api.getCustomerListWithBalanceTax(user.UsrName);
//         const body = res?.CustomerListWithBalanceTaxjs ?? res ?? {};

//         const t = body.Table ?? [];
//         const t1 = body.Table1 ?? [];
//         const t2 = body.Table2 ?? [];

//         if (!mounted) return;
//         setCustomers(t || []);
//         setPaymentModes(t1 || []);
//         setSalesTypes(t2 || []);

//         if (t1?.length) setPayMode(prev => prev || t1[0].Mode || '');
//         if (t2?.length) {
//           const def = t2[0];
//           setSalesType(prev => prev || def.TypeStr || '');
//           setSalesTypeQuery(prev => prev || def.TypeName || '');
//         }
//       } catch (err) {
//         console.error('Failed to fetch customers', err);
//         if (!mounted) return;
//         setError('Failed to fetch customers.');
//       } finally {
//         if (mounted) setLoading(false);
//       }
//     };

//     fetchData();
//     return () => { mounted = false; };
//   }, [user.UsrName]);

//   // Filter customers
//   useEffect(() => {
//     if (customerSearch) {
//       const results = customers.filter(c =>
//         c.Vname.toLowerCase().includes(customerSearch.toLowerCase()) ||
//         (c.Mobile || '').includes(customerSearch)
//       );
//       const exactMatch = selectedCustomer && selectedCustomer.Vname.toLowerCase() === customerSearch.toLowerCase();
//       setFilteredCustomers(exactMatch ? [] : results);
//       setIsDropdownOpen(!exactMatch && results.length > 0);
//       setHighlightIndex(-1);
//     } else {
//       setFilteredCustomers(customers);
//       setIsDropdownOpen(customers.length > 0);
//       setHighlightIndex(-1);
//     }
//   }, [customerSearch, customers, selectedCustomer]);

//   const handleCustomerSelect = (customer) => {
//     setSelectedCustomer(customer);
//     setCustomerSearch(customer.Vname);
//     setEditableAddress(`${customer.Address1 || '-'}, ${customer.City || '-'}`);
//     setIsDropdownOpen(false);
//     setFilteredCustomers([]);
//     setHighlightIndex(-1);

//     setTimeout(() => {
//       if (createOrderBtnRef.current) createOrderBtnRef.current.focus();
//     }, 50);
//   };

//   // Keyboard nav
//   const handleKeyDown = (e) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       if (highlightIndex >= 0 && filteredCustomers[highlightIndex]) {
//         handleCustomerSelect(filteredCustomers[highlightIndex]);
//       } else if (filteredCustomers.length === 1) {
//         handleCustomerSelect(filteredCustomers[0]);
//       } else if (selectedCustomer) {
//         handleCreateSalesOrder();
//       }
//       return;
//     }

//     if (!isDropdownOpen || filteredCustomers.length === 0) return;

//     if (e.key === 'ArrowDown') {
//       e.preventDefault();
//       setHighlightIndex(prev => (prev < filteredCustomers.length - 1 ? prev + 1 : 0));
//     } else if (e.key === 'ArrowUp') {
//       e.preventDefault();
//       setHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredCustomers.length - 1));
//     }
//   };

//   // Create Sales Order
//   const handleCreateSalesOrder = async () => {
//     if (!selectedCustomer) {
//       setError('Please select a customer.');
//       return;
//     }

//     const amountAsNumber = parseFloat(amount);
//     if (amount && (isNaN(amountAsNumber) || amountAsNumber < 0)) {
//       setError('Please enter a valid amount.');
//       return;
//     }

//     if (!gdId) {
//       setError('Godown not selected. Please select a godown first.');
//       return;
//     }

//     setError('');
//     setLoading(true);

//     try {
//       const selType = salesTypes.find(t => t.TypeStr === salesType) || { TypeStr: '', TypeName: '' };

//       const payload = {
//   User: user.UsrName,
//   Vid: selectedCustomer.Vid,
//   VName: selectedCustomer.Vname,
//   Address: editableAddress,
//   Phone: selectedCustomer.Mobile,
//   OrderDate: orderDate,
//   SessionId: '9944010155',
//   amountPaid: amount ? parseFloat(amount) : 0, // numeric 0 if empty
//   Paymode: payMode || '',                        // always send
//   Refno: refNo || '',                             // always send, even if empty
//   GdId: Number(gdId),
//   SubTypecode: selType.TypeStr,
//   Subtypename: selType.TypeName
// };

//       console.debug('createSalesOrder payload:', payload);
//       const createResult = await api.createSalesOrder(payload);

//       const orderTable =
//         createResult?.Table ??
//         createResult?.MobCreateorderlistTaxjs?.Table ??
//         createResult?.data?.MobCreateorderlistTaxjs?.Table ??
//         [];

//       if (orderTable.length > 0) {
//         const newOrderId = orderTable[0].POId;
//         const selectedOrderData = await api.getSelectedSalesOrder(newOrderId);

//         const ctx = {
//           id: newOrderId,
//           customerId: selectedCustomer.Vid,
//           orderDate,
//           customer: selectedCustomer,
//           orderDetails: selectedOrderData
//         };

//         setEditingContext(ctx);
//         setEditingContextLocal(ctx);
//         setSuccess(`Sales Order created successfully with ID: ${newOrderId}`);
//         setFocusAddBtn(true);

//         setTimeout(() => {
//           setView('new-salesorder');
//           setOpenAddItemModal(true);
//         }, 100);
//       } else {
//         setError('Failed to create order.');
//       }
//     } catch (err) {
//       console.error(err);
//       setError('An error occurred. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // filter sales type list
//   const filteredSalesTypes = salesTypes.filter(t =>
//     t.TypeName.toLowerCase().includes(salesTypeQuery.toLowerCase())
//   );

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//       {/* Header */}
//       <div className="p-5 bg-white shadow-md border-b">
//         <h2 className="text-2xl font-bold text-center text-gray-800 tracking-tight">📝 Create New Sales Order</h2>
//       </div>

//       <div className="flex justify-center mt-8">
//         <div className="w-full max-w-xl p-3 bg-white shadow-lg rounded-2xl">

//           {/* Delivery Date */}
//           <div className="mb-6">
//             <label className="block mb-2 text-sm font-semibold text-gray-700">📅 Delivery Date*</label>
//             <input
//               type="date"
//               value={orderDate}
//               onChange={(e) => setOrderDate(e.target.value)}
//               className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Customer Search */}
//           <div className="relative mb-6 text-gray-500 text-sm">
//             <InputWithIcon
//               label="Search & Select Customer*"
//               value={customerSearch}
//               ref={customerInputRef}
//               onChange={(e) => setCustomerSearch(e.target.value)}
//               onFocus={() => setIsDropdownOpen(true)}
//               onKeyDown={handleKeyDown}
//               icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
//               </svg>}
//             />
//             {isDropdownOpen && filteredCustomers.length > 0 && (
//               <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto divide-y divide-gray-100">
//                 {filteredCustomers.map((c, idx) => (
//                   <li
//                     key={c.Vid}
//                     onMouseDown={() => handleCustomerSelect(c)}
//                     className={`flex flex-col px-3 py-2 cursor-pointer transition-all duration-150 ${
//                       idx === highlightIndex ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-blue-50 hover:text-blue-600'
//                     } rounded-lg`}
//                   >
//                     <span className="font-medium text-gray-800">{c.Vname}</span>
//                     <span className="text-xs text-gray-500">{c.Mobile}</span>
//                   </li>
//                 ))}
//               </ul>
//             )}
//           </div>

//           {/* Customer Details */}
//           {selectedCustomer && (
//             <div className="p-1 mb-6 bg-white rounded-xl shadow-sm">
//               <label className="block mb-2 text-sm font-semibold text-gray-800">Delivery Address*</label>
//               <textarea
//                 value={editableAddress}
//                 onChange={(e) => setEditableAddress(e.target.value)}
//                 rows="3"
//                 className="w-full p-3 mb-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//               />

//               <div className="space-y-2 text-sm">
//                 <p><span className="font-medium text-gray-800">📞 Mobile:</span> {selectedCustomer.Mobile}</p>
//                 <p>
//                   <span className="font-medium text-gray-800">💰 Current Balance:</span>
//                   <span className={`ml-1 font-bold ${selectedCustomer.Balance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
//                     {selectedCustomer.Balance.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
//                   </span>
//                 </p>
//               </div>

//               {/* Payment Section */}
//               <div className="mt-6">
//                 <h4 className="flex items-center gap-2 pb-2 mb-4 text-base font-semibold text-gray-800">💳 Payment (Optional)</h4>
//                 <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
//                   <InputWithIcon label="Ref No" value={refNo} onChange={(e) => setRefNo(e.target.value)} />
//                   <InputWithIcon label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />

//                   <div>
//                     <label className="block mb-2 text-sm font-bold text-gray-700">Pay Mode</label>
//                     <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
//                       {paymentModes.map((mode, idx) => (
//                         <option key={idx} value={mode.Mode}>{mode.Mode}</option>
//                       ))}
//                     </select>
//                   </div>

//                   {/* ✅ Searchable Sales Type */}
//                   <div className="relative">
//                     <label className="block mb-2 text-sm font-bold text-gray-700">Sales Type</label>
//                     <input
//                       value={salesTypeQuery}
//                       onChange={(e) => {
//                         setSalesTypeQuery(e.target.value);
//                         setSalesDropdownOpen(true);
//                       }}
//                       onFocus={() => setSalesDropdownOpen(true)}
//                       placeholder="Search sales type..."
//                       className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     />
//                     {salesDropdownOpen && (
//                       <ul className="absolute z-10 w-full bg-white border rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg">
//                         {filteredSalesTypes.length > 0 ? (
//                           filteredSalesTypes.map((type) => (
//                             <li
//                               key={type.TypeStr}
//                               onClick={() => {
//                                 setSalesType(type.TypeStr);
//                                 setSalesTypeQuery(type.TypeName);
//                                 setSalesDropdownOpen(false);
//                               }}
//                               className="p-2 hover:bg-blue-100 cursor-pointer"
//                             >
//                               {type.TypeName}
//                             </li>
//                           ))
//                         ) : (
//                           <li className="p-2 text-gray-500">No results</li>
//                         )}
//                       </ul>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Action Buttons */}
//           <div className="flex gap-3 pt-3 justify-end">
//             <Button ref={createOrderBtnRef} onClick={handleCreateSalesOrder} disabled={!selectedCustomer || loading} className="px-5 py-2 text-sm font-semibold">
//               {loading ? 'Creating...' : '✅ Create Sales Order'}
//             </Button>
//             <Button onClick={() => setView('salesorders')} className="px-5 py-2 text-sm font-semibold bg-gray-500 hover:bg-gray-600">❌ Cancel</Button>
//           </div>
//         </div>
//       </div>

//       {/* Add Item Modal */}
//       {editingContextLocal && (
//         <OrderNewItem
//           setView={setView}
//           context={editingContextLocal}
//           user={user}
//           setSuccess={setSuccess}
//           setError={setError}
//           openModal={openAddItemModal}
//           setOpenModal={setOpenAddItemModal}
//           focusAddBtn={focusAddBtn}
//         />
//       )}
//     </div>
//   );
// };

// export default AddSalesOrderPage;


